<script lang="ts">
	import { RepositoryHeaderProps } from '../models/props';
	import { appStore } from '../stores/appStore';
	import { AppApiClient } from '../api_contracts/app.client';
	import { onMount } from 'svelte';

	let { repositoryId, ownerUsername, name, visibility, watchCount, starCount, forkCount, avatarUrl }: RepositoryHeaderProps = $props();

	let client: AppApiClient | null = $state(null);
	let token: string = $state('');
	let isStarred = $state(false);
	let isWatched = $state(false);
	let isStarLoading = $state(false);
	let isWatchLoading = $state(false);

	// Subscribe to store
	appStore.subscribe(state => {
		client = state.client;
		token = state.token;
	});

	// Check if repository is starred and watched
	async function checkRepositoryStatus() {
		if (!client || !token || !repositoryId) return;

		try {
			// Check if starred
			const starredResponse = await client.isRepositoryStarred({
				token,
				repositoryId
			});
			if (starredResponse.response?.successFull) {
				isStarred = starredResponse.response.isStarred || false;
			}

			// Check if watched
			const watchedResponse = await client.isRepositoryWatched({
				token,
				repositoryId
			});
			if (watchedResponse.response?.successFull) {
				isWatched = watchedResponse.response.isWatched || false;
			}
		} catch (error) {
			console.error('Error checking repository status:', error);
		}
	}

	async function toggleStar() {
		if (!client || !token || isStarLoading) return;

		try {
			isStarLoading = true;
			if (isStarred) {
				const response = await client.unstarRepository({ token, repositoryId });
				if (response.response?.successFull) {
					isStarred = false;
					starCount = Math.max(0, starCount - 1);
				}
			} else {
				const response = await client.starRepository({ token, repositoryId });
				if (response.response?.successFull) {
					isStarred = true;
					starCount = starCount + 1;
				}
			}
		} catch (error) {
			console.error('Error toggling star:', error);
		} finally {
			isStarLoading = false;
		}
	}

	async function toggleWatch() {
		if (!client || !token || isWatchLoading) return;

		try {
			isWatchLoading = true;
			if (isWatched) {
				const response = await client.unwatchRepository({ token, repositoryId });
				if (response.response?.successFull) {
					isWatched = false;
					watchCount = Math.max(0, watchCount - 1);
				}
			} else {
				const response = await client.watchRepository({ token, repositoryId });
				if (response.response?.successFull) {
					isWatched = true;
					watchCount = watchCount + 1;
				}
			}
		} catch (error) {
			console.error('Error toggling watch:', error);
		} finally {
			isWatchLoading = false;
		}
	}

	function handleFork() {
		if (!token) {
			alert('Please login to fork this repository');
			return;
		}
		// Navigate to fork page
		window.location.hash = `#/users/${ownerUsername}/repositories/${name}/fork`;
	}

	onMount(() => {
		checkRepositoryStatus();
	});
</script>

<!-- <header class="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700"> -->
<header class="mb-6 pb-4 ">
	<div class="flex flex-col md:flex-row md:justify-between md:items-center">
		<div class="mb-4 md:mb-0">
			<h1 class="text-2xl font-semibold flex items-center">
				{#if avatarUrl}
					<img src={avatarUrl} alt="{name} avatar" class="w-8 h-8 rounded-lg mr-3" />
				{:else}
					<div class="w-8 h-8 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center mr-3">
						<svg class="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
							<path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" />
						</svg>
					</div>
				{/if}
				<a href="#/users/{ownerUsername}" class="text-blue-600 dark:text-blue-400 hover:underline">{ownerUsername}</a>
				<span class="text-gray-600 dark:text-gray-300 mx-1">/</span>
				<span class="font-bold text-gray-900 dark:text-gray-100">{name}</span>
				<span
					class="text-xs border border-gray-300 dark:border-gray-600 rounded-full px-2 py-0.5 ml-3 text-gray-600 dark:text-gray-400 uppercase"
				>
					{visibility}
				</span>
			</h1>
		</div>

		<div class="flex space-x-2 text-sm">
			<button
				onclick={toggleWatch}
				disabled={isWatchLoading || !token}
				class="flex items-center px-3 py-1.5 {isWatched ? 'bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 dark:hover:bg-blue-900/30 text-blue-700 dark:text-blue-300' : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'} rounded-md border border-gray-300 dark:border-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
			>
				{#if isWatchLoading}
					<div class="w-4 h-4 mr-1.5 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
				{:else}
					<svg class="w-4 h-4 mr-1.5" fill={isWatched ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
						/>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
						/>
					</svg>
				{/if}
				{isWatched ? 'Unwatch' : 'Watch'}
				<span class="ml-2 px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">{watchCount}</span>
			</button>
			<button
				onclick={toggleStar}
				disabled={isStarLoading || !token}
				class="flex items-center px-3 py-1.5 {isStarred ? 'bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/20 dark:hover:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' : 'bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200'} rounded-md border border-gray-300 dark:border-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
			>
				{#if isStarLoading}
					<div class="w-4 h-4 mr-1.5 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
				{:else}
					<svg class="w-4 h-4 mr-1.5" fill={isStarred ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 20 20">
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
						/>
					</svg>
				{/if}
				{isStarred ? 'Unstar' : 'Star'}
				<span class="ml-2 px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">{starCount}</span>
			</button>
			<button
				onclick={handleFork}
				disabled={!token}
				class="flex items-center px-3 py-1.5 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md border border-gray-300 dark:border-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
			>
				<svg class="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
					/>
				</svg>
				Fork
				<span class="ml-2 px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">{forkCount}</span>
			</button>
		</div>
	</div>
</header>
